import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { createTileLayer } from '../utils/mapTiles';

export default function MapView({ hubs = [], stops = [], selectedStopId, onSelectStop }) {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markersRef = useRef({});
  const polylineRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!leafletMap.current) {
      // Default view centered on Lausanne / Renens region (Switzerland)
      leafletMap.current = L.map(mapRef.current, {
        center: [46.52, 6.63],
        zoom: 12,
        zoomControl: false
      });

      L.control.zoom({ position: 'topright' }).addTo(leafletMap.current);

      createTileLayer('pale').addTo(leafletMap.current);
    }

    const map = leafletMap.current;

    // Clear existing markers
    Object.values(markersRef.current).forEach(m => map.removeLayer(m));
    markersRef.current = {};
    if (polylineRef.current) {
      map.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }

    const routeLatLngs = [];

    // Render Hub Markers (Simple square marker without emoji)
    hubs.forEach(hub => {
      if (hub.latitude && hub.longitude) {
        const hubIcon = L.divIcon({
          className: 'custom-hub-icon',
          html: `<div style="background:#0f172a; border:3px solid #38bdf8; border-radius:4px; width:32px; height:32px; display:flex; align-items:center; justify-content:center; box-shadow:0 4px 12px rgba(0,0,0,0.6);"><div style="background:#38bdf8; width:13px; height:13px; border-radius:2px;"></div></div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 16]
        });

        const marker = L.marker([hub.latitude, hub.longitude], { icon: hubIcon })
          .bindPopup(`<b>${hub.name}</b> (Hub)<br/>${hub.address || ''}, ${hub.city || ''}`)
          .addTo(map);

        markersRef.current[`hub-${hub.id}`] = marker;
        routeLatLngs.push([hub.latitude, hub.longitude]);
      }
    });

    // Render Stop Markers
    stops.forEach((stop, idx) => {
      if (stop.latitude && stop.longitude) {
        const isDelivered = stop.status === 'delivered';
        const isFailed = stop.status === 'failed';
        
        let bgColor = '#3b82f6'; // Blue pending
        if (isDelivered) bgColor = '#22c55e'; // Green delivered
        if (isFailed) bgColor = '#ef4444'; // Red failed

        const iconHtml = `<div style="background:${bgColor}; color:#ffffff; border:2px solid #ffffff; border-radius:50%; width:28px; height:28px; display:flex; align-items:center; justify-content:center; font-weight:bold; font-size:12px; box-shadow:0 3px 8px rgba(0,0,0,0.4);">${isDelivered ? '✓' : stop.sequence_order || idx + 1}</div>`;

        const stopIcon = L.divIcon({
          className: 'custom-stop-icon',
          html: iconHtml,
          iconSize: [28, 28],
          iconAnchor: [14, 14]
        });

        const marker = L.marker([stop.latitude, stop.longitude], { icon: stopIcon })
          .bindPopup(`
            <div style="font-family:sans-serif; padding:4px;">
              <strong style="font-size:13px; color:#0f172a;">${stop.client_name || 'Client'}</strong><br/>
              <span style="font-size:11px; color:#64748b;">${stop.address || ''}, ${stop.city || ''}</span><br/>
              <div style="margin-top:6px; font-size:11px; display:flex; justify-content:space-between;">
                <span>Statut: <b style="color:${bgColor};">${stop.status.toUpperCase()}</b></span>
                <span>Poids: <b>${stop.weight_kg || 0} kg</b></span>
              </div>
            </div>
          `)
          .addTo(map);

        marker.on('click', () => {
          if (onSelectStop) onSelectStop(stop.id);
        });

        markersRef.current[`stop-${stop.id}`] = marker;
        routeLatLngs.push([stop.latitude, stop.longitude]);
      }
    });

    // Draw route line between stops (Thin, solid, not dotted)
    if (routeLatLngs.length > 1) {
      polylineRef.current = L.polyline(routeLatLngs, {
        color: '#38bdf8',
        weight: 2.5,
        opacity: 0.85,
        dashArray: null
      }).addTo(map);
    }
  }, [hubs, stops]);

  return <div ref={mapRef} className="map-canvas" />;
}
